import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight, FaArrowLeft, FaRedo, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import { getRevealProps } from '../motion/scrollMotion';
import { sendSubmission } from '../utils/mailer';
import './ServiceDetailPage.css';
import './AssessmentTool.css';

// Turn the answers + computed result into a flat set of fields for the lead email,
// so our team can see exactly what the respondent selected and tailor support.
const buildLeadFields = (lead, meta, result, answers, questions) => {
  const responses = questions.map((q, i) => {
    const chosen = q.options.find((o) => o.value === answers[q.id]);
    return `Q${i + 1} [${q.domain}]: ${chosen ? chosen.label : '—'}`;
  });
  const fields = {
    Name: lead.name,
    Email: lead.email,
    Company: lead.company,
    Assessment: meta.heroTitle,
    'Maturity Result': result.tier,
  };
  if (typeof result.overallPct === 'number') fields['Overall Score'] = `${result.overallPct}%`;
  if (result.weakestDomain) fields['Weakest Area'] = result.weakestDomain;
  if (result.domainScores) {
    fields['Domain Scores'] = result.domainScores
      .map((d) => `${d.domain}: ${d.avg.toFixed(1)}/5`)
      .join('\n');
  }
  fields['Responses'] = responses.join('\n');
  return fields;
};

const AssessmentTool = ({ meta, questions, computeResult }) => {
  const [step, setStep] = useState(0); // index into questions, or questions.length for results
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: '', email: '', company: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [leadError, setLeadError] = useState('');

  const total = questions.length;
  const isResults = step >= total;
  const current = !isResults ? questions[step] : null;
  const progressPct = Math.round((step / total) * 100);

  const selectOption = (value) => {
    setAnswers((prev) => ({ ...prev, [current.id]: value }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const restart = () => {
    setAnswers({});
    setStep(0);
    setLead({ name: '', email: '', company: '' });
    setSent(false);
    setLeadError('');
  };

  const result = isResults ? computeResult(answers, questions) : null;

  const submitLead = async (e) => {
    e.preventDefault();
    if (sending) return;
    setLeadError('');
    setSending(true);
    try {
      await sendSubmission({
        source: meta.source || `${meta.heroTitle} (Free Tool)`,
        fields: buildLeadFields(lead, meta, result, answers, questions),
        replyTo: lead.email,
      });
      setSent(true);
    } catch (err) {
      setLeadError('Could not send your results just now. Please try again, or reach us at info@proveninfosec.com.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="svc-page">
      <PageNavbar />

      <section className="svc-hero at-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container">
          <motion.div className="at-hero-text" {...getRevealProps('fadeUp')}>
            <span className="svc-eyebrow">{meta.eyebrow}</span>
            <h1 className="svc-hero-title">{meta.heroTitle}</h1>
            <p className="svc-hero-desc">{meta.heroDesc}</p>
          </motion.div>
        </div>
      </section>

      <section className="svc-section at-quiz-section">
        <div className="svc-container at-quiz-container">
          {!isResults ? (
            <div className="at-quiz-card">
              <div className="at-progress-track">
                <div className="at-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="at-progress-label">Question {step + 1} of {total}</div>

              {/* Keyed enter-only animation. No AnimatePresence/exit: an exit that
                  stalls would keep the previous question's buttons mounted while
                  `step` advances, causing every answer to be written to the wrong
                  question. This guarantees only the current question is in the DOM. */}
              <motion.div
                key={current.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25 }}
              >
                {current.domain && <span className="at-question-domain">{current.domain}</span>}
                <h2 className="at-question">{current.prompt}</h2>
                <div className="at-options">
                  {current.options.map((o) => (
                    <button
                      type="button"
                      key={o.label}
                      className={`at-option ${answers[current.id] === o.value ? 'is-selected' : ''}`}
                      onClick={() => selectOption(o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </motion.div>

              {step > 0 && (
                <button type="button" className="at-back-btn" onClick={goBack}>
                  <FaArrowLeft /> Back
                </button>
              )}
            </div>
          ) : (
            <motion.div className="at-results-card" {...getRevealProps('fadeUp')}>
              <span className="svc-card-tag">{meta.resultsIntro}</span>
              <h2 className="at-results-tier">{result.tier}</h2>

              {typeof result.pct === 'number' && (
                <div className="at-score-bar-track">
                  <div className="at-score-bar-fill" style={{ width: `${result.pct}%` }} />
                </div>
              )}
              {typeof result.overallPct === 'number' && (
                <div className="at-score-bar-track">
                  <div className="at-score-bar-fill" style={{ width: `${result.overallPct}%` }} />
                </div>
              )}

              <p className="at-results-desc">{result.tierDesc}</p>

              {result.domainScores && (
                <div className="at-domain-breakdown">
                  <h3>Breakdown by {meta.domainLabel || 'Domain'}</h3>
                  {result.domainScores.map((d) => (
                    <div className="at-domain-row" key={d.domain}>
                      <span className="at-domain-name">{d.domain}</span>
                      <div className="at-domain-bar-track">
                        <div className="at-domain-bar-fill" style={{ width: `${(d.avg / 5) * 100}%` }} />
                      </div>
                      <span className="at-domain-value">{d.avg.toFixed(1)}/5</span>
                    </div>
                  ))}
                  <p className="at-domain-note">
                    Your lowest-scoring area is <strong>{result.weakestDomain}</strong> — usually the
                    highest-leverage place to start.
                  </p>
                </div>
              )}

              {/* Honesty disclaimer — this is an indicative self-assessment, not an audit. */}
              <div className="at-disclaimer">
                <FaInfoCircle className="at-disclaimer-icon" />
                <p>
                  {meta.disclaimer ||
                    'This maturity level is indicative only — it is estimated from the answers you provided in this short self-assessment. It is not a formal audit, certification, or a validated assessment of your controls. A hands-on review by our team may reach a different conclusion. Use it as a starting point for the conversation.'}
                </p>
              </div>

              {/* Lead capture — email the result + a tailored action plan. */}
              {!sent ? (
                <form className="at-lead" onSubmit={submitLead}>
                  <h3 className="at-lead-title">Get your results and a tailored action plan</h3>
                  <p className="at-lead-sub">
                    We will send a copy of this assessment and a senior consultant will follow up with
                    a prioritised, no-obligation plan for your {meta.frameworkShort || 'program'}.
                  </p>
                  <div className="at-lead-row">
                    <input
                      type="text"
                      placeholder="Full name"
                      required
                      value={lead.name}
                      onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    />
                    <input
                      type="email"
                      placeholder="Work email"
                      required
                      value={lead.email}
                      onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Company"
                    required
                    value={lead.company}
                    onChange={(e) => setLead({ ...lead, company: e.target.value })}
                  />
                  <button type="submit" className="svc-btn svc-btn-primary at-lead-btn" disabled={sending}>
                    {sending ? 'Sending…' : <>Send my results &amp; action plan <FaArrowRight /></>}
                  </button>
                  {leadError && <p className="at-lead-error" role="alert">{leadError}</p>}
                </form>
              ) : (
                <div className="at-lead-sent">
                  <FaCheckCircle className="at-lead-sent-icon" />
                  <div>
                    <strong>Thanks — your results are on the way to our team.</strong>
                    <div>A senior consultant will review your answers and follow up within one business day.</div>
                  </div>
                </div>
              )}

              <div className="at-results-actions">
                <Link to={result.ctaHref} className="svc-btn svc-btn-ghost">
                  {result.ctaLabel} <FaArrowRight />
                </Link>
                <button type="button" className="svc-btn svc-btn-ghost-light" onClick={restart}>
                  <FaRedo /> Retake
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AssessmentTool;
